import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarBlank, CaretLeft, CaretRight, CaretDown, Clock } from "phosphor-react";
import { AnimatePresence, motion } from "framer-motion";

/* ───────────────────────── Helpers ───────────────────────── */

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addDays(d, days) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
}

function addMonths(d, months) {
  const nd = new Date(d);
  const day = nd.getDate();
  nd.setDate(1);
  nd.setMonth(nd.getMonth() + months);
  const max = new Date(nd.getFullYear(), nd.getMonth() + 1, 0).getDate();
  nd.setDate(Math.min(day, max));
  return nd;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Parse "YYYY-MM-DDTHH:MM" or any Date-parseable string into a local Date.
function parseValueToLocalDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2}))?/.exec(s);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const hh = m[4] != null ? Number(m[4]) : 0;
    const mm = m[5] != null ? Number(m[5]) : 0;
    const dt = new Date(y, mo, d, hh, mm, 0, 0);
    if (isNaN(dt.getTime())) return null;
    return dt;
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
}

function toDatetimeLocalString(d) {
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatTriggerLabel(d, locale = "en-US") {
  if (!d) return "";
  const datePart = d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  const suffix = d.getHours() < 12 ? "AM" : "PM";
  return `${datePart}, ${hh}:${mm} ${suffix}`;
}

function monthLabel(d, locale = "en-US") {
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

function rotateWeekdays(base, weekStartsOn) {
  if (!weekStartsOn) return base;
  return base.slice(weekStartsOn).concat(base.slice(0, weekStartsOn));
}

function buildMonthGrid(monthDate, weekStartsOn = 0) {
  const base = startOfMonth(monthDate);
  const firstDow = base.getDay();
  const offset = (firstDow - weekStartsOn + 7) % 7;
  const gridStart = addDays(base, -offset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({ date, inMonth: date.getMonth() === monthDate.getMonth(), day: date.getDate() });
  }
  return cells;
}

/* ───────────────────────── Component ───────────────────────── */

export default function DateTimePicker({
  value = "",
  onChange,
  className = "",
  locale = "en-US",
  placeholder = "Select date & time",
  weekStartsOn = 0,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [draft, setDraft] = useState(() => parseValueToLocalDate(value) || new Date());
  const [hour, setHour] = useState(() => pad2((parseValueToLocalDate(value) || new Date()).getHours()));
  const [minute, setMinute] = useState(() => pad2((parseValueToLocalDate(value) || new Date()).getMinutes()));
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(parseValueToLocalDate(value) || new Date()));

  // Outside click + Escape close
  useEffect(() => {
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Re-sync when opening or when external value changes while closed
  useEffect(() => {
    const d = parseValueToLocalDate(value) || new Date();
    setDraft(d);
    setHour(pad2(d.getHours()));
    setMinute(pad2(d.getMinutes()));
    setViewMonth(startOfMonth(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  const triggerLabel = useMemo(() => {
    const d = parseValueToLocalDate(value);
    return d ? formatTriggerLabel(d, locale) : placeholder;
  }, [value, locale, placeholder]);

  const daysHeader = useMemo(() => {
    const base = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    return rotateWeekdays(base, weekStartsOn);
  }, [weekStartsOn]);

  const prevMonth = () => setViewMonth((m) => startOfMonth(addMonths(m, -1)));
  const nextMonth = () => setViewMonth((m) => startOfMonth(addMonths(m, 1)));

  const onDayClick = (date) => {
    if (!date) return;
    const merged = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      parseInt(hour, 10) || 0,
      parseInt(minute, 10) || 0,
      0,
      0
    );
    setDraft(merged);
    setViewMonth(startOfMonth(merged));
  };

  const setNow = () => {
    const now = new Date();
    setDraft(now);
    setHour(pad2(now.getHours()));
    setMinute(pad2(now.getMinutes()));
    setViewMonth(startOfMonth(now));
  };

  const apply = () => {
    if (!onChange) return setOpen(false);
    const h = Math.max(0, Math.min(23, parseInt(hour, 10) || 0));
    const m = Math.max(0, Math.min(59, parseInt(minute, 10) || 0));
    const out = new Date(
      draft.getFullYear(),
      draft.getMonth(),
      draft.getDate(),
      h,
      m,
      0,
      0
    );
    onChange(toDatetimeLocalString(out));
    setOpen(false);
  };

  const cancel = () => {
    const d = parseValueToLocalDate(value) || new Date();
    setDraft(d);
    setHour(pad2(d.getHours()));
    setMinute(pad2(d.getMinutes()));
    setViewMonth(startOfMonth(d));
    setOpen(false);
  };

  const handleHourChange = (raw) => {
    const v = String(raw).replace(/\D/g, "").slice(0, 2);
    setHour(v);
  };
  const handleHourBlur = () => {
    const n = Math.max(0, Math.min(23, parseInt(hour, 10) || 0));
    setHour(pad2(n));
  };
  const handleMinuteChange = (raw) => {
    const v = String(raw).replace(/\D/g, "").slice(0, 2);
    setMinute(v);
  };
  const handleMinuteBlur = () => {
    const n = Math.max(0, Math.min(59, parseInt(minute, 10) || 0));
    setMinute(pad2(n));
  };

  const renderCalendar = () => {
    const cells = buildMonthGrid(viewMonth, weekStartsOn);
    return (
      <div className="w-[280px] max-w-[88vw]">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-semibold text-neutral-800 dark:text-neutral-100">
            {monthLabel(viewMonth, locale)}
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-900"
              aria-label="Previous month"
            >
              <CaretLeft size={14} className="text-neutral-600 dark:text-neutral-400" />
            </button>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-900"
              aria-label="Next month"
            >
              <CaretRight size={14} className="text-neutral-600 dark:text-neutral-400" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 text-center text-[10px] font-medium text-neutral-400 dark:text-neutral-500">
          {daysHeader.map((d) => (
            <div key={d} className="py-0.5">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-1">
          {cells.map((cell) => {
            const selected = isSameDay(cell.date, draft);
            const today = isSameDay(cell.date, new Date());
            const muted = !cell.inMonth;
            return (
              <div key={cell.date.toISOString()} className="relative flex items-center justify-center h-8">
                <button
                  type="button"
                  onClick={() => onDayClick(cell.date)}
                  className={[
                    "relative z-10 w-8 h-8 text-[12px] rounded-full transition",
                    selected
                      ? "bg-yellow-600 text-white"
                      : "hover:bg-neutral-100 dark:hover:bg-neutral-900",
                    muted && !selected
                      ? "text-neutral-300 dark:text-neutral-600"
                      : "text-neutral-800 dark:text-neutral-100",
                    today && !selected ? "ring-1 ring-yellow-400/70" : "",
                    "focus:outline-none focus:ring-2 focus:ring-yellow-400/30",
                  ].join(" ")}
                >
                  {cell.day}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "w-full h-10 px-2.5 rounded-xl",
          "border border-neutral-100 dark:border-neutral-900",
          "bg-white dark:bg-neutral-950",
          "text-[13px] text-neutral-600 dark:text-neutral-100",
          "inline-flex items-center gap-2",
          "hover:bg-neutral-50 dark:hover:bg-neutral-900/40",
          "focus:outline-none focus:ring-2 focus:ring-yellow-400/25",
        ].join(" ")}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarBlank size={15} className="text-neutral-700 dark:text-neutral-400" />
        <span className="truncate">{triggerLabel}</span>
        <span className="ml-auto">
          <CaretDown size={15} className="text-neutral-700 dark:text-neutral-400" />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className={[
              "absolute mt-2 text-start z-50",
              "w-[320px] max-w-[96vw]",
              "bg-white dark:bg-neutral-950",
              "border border-neutral-100 dark:border-neutral-900",
              "rounded-2xl shadow-2xl overflow-hidden",
            ].join(" ")}
            role="dialog"
          >
            <div className="p-3">
              {/* Time + Now */}
              <div className="flex items-end gap-2 mb-3">
                <div className="flex-1">
                  <div className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                    Time
                  </div>
                  <div
                    className={[
                      "w-full h-9 px-2.5 rounded-xl",
                      "border border-neutral-100 dark:border-neutral-900",
                      "bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-100",
                      "inline-flex items-center gap-2",
                      "focus-within:ring-2 focus-within:ring-yellow-400/25",
                    ].join(" ")}
                  >
                    <Clock size={13} className="text-neutral-500 dark:text-neutral-400" />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={hour}
                      onChange={(e) => handleHourChange(e.target.value)}
                      onBlur={handleHourBlur}
                      placeholder="HH"
                      className="w-7 bg-transparent border-0 outline-none text-[13px] tabular-nums text-center"
                    />
                    <span className="text-neutral-400 text-[13px]">:</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={minute}
                      onChange={(e) => handleMinuteChange(e.target.value)}
                      onBlur={handleMinuteBlur}
                      placeholder="MM"
                      className="w-7 bg-transparent border-0 outline-none text-[13px] tabular-nums text-center"
                    />
                    <span className="ml-auto text-[10px] text-neutral-400">24h</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={setNow}
                  className={[
                    "h-9 px-2.5 rounded-xl text-[12px]",
                    "border border-neutral-100 dark:border-neutral-900",
                    "text-neutral-700 dark:text-neutral-200",
                    "hover:bg-neutral-100 dark:hover:bg-neutral-900",
                  ].join(" ")}
                >
                  Now
                </button>
              </div>

              {renderCalendar()}

              {/* Footer */}
              <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-900 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  className="text-[12px] px-2.5 py-1.5 rounded-lg text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={apply}
                  className="text-[12px] px-3 py-1.5 rounded-lg bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                >
                  Apply
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
